import type React from 'react';
import { useTranslation } from 'react-i18next';
import { MigrationAction } from './MigrationAction';
import { MigrationStateLayout } from './MigrationStateLayout';

interface BlockedStateProps {
  isAdmin: boolean;
  /** i18n key naming why the API would refuse; when set, no action is offered. */
  blockedReasonKey: string | null;
  isStarting: boolean;
  onStart: () => void;
  currencyNotice?: string;
}

/**
 * The last attempt was closed before the merchant could approve it.
 *
 * Never a dead end. The state is reached whenever an attempt is closed without
 * a verdict — a store of the organization uninstalled, then reinstalled, for
 * instance — and in most of those the subscription is live and the backend says
 * a new attempt can be started. So this offers the same restart the declined
 * and expired states do, and falls back to naming the blocker when the API
 * would actually refuse.
 */
export const BlockedState: React.FC<BlockedStateProps> = ({
  isAdmin,
  blockedReasonKey,
  isStarting,
  onStart,
  currencyNotice,
}) => {
  const { t } = useTranslation();

  return (
    <MigrationStateLayout
      tone="error"
      status={t('settings.subscription.successFeeMigration.blocked.body')}
      currencyNotice={currencyNotice}
    >
      <MigrationAction
        isAdmin={isAdmin}
        blockedReasonKey={blockedReasonKey}
        isStarting={isStarting}
        onAct={onStart}
        actionKey="settings.subscription.successFeeMigration.retry.action"
        viewerHelperKey="settings.subscription.successFeeMigration.retry.viewerHelper"
      />
    </MigrationStateLayout>
  );
};
